/**
 * Experience Data Transformations
 *
 * Transforms API response data into the wizard's expected formData shape.
 * Used by EditExperiencePage to hydrate ExperienceCreationWizard with existing data.
 *
 * Story: SCRUM-827 (FIX-F05: Edit-via-wizard route + data transformation)
 * Story: SCRUM-832 (FIX-F08c: keyLocations editing — route data transformation)
 */

import { metersToMiles, metersToFeet } from './unitConversions';

/**
 * Maps pricing config formula_type to wizard pricingModel values.
 * Must stay in sync with FORMULA_TYPE_TO_WIZARD in useExperienceAutoSave.js.
 */
const FORMULA_TYPE_TO_WIZARD = {
  per_person_flat: 'per_person',
  flat_group: 'flat_group',
  base_additional: 'base_plus_per',
  base_covers_n: 'base_covers_n',
};

/**
 * Maps section type names to wizard formData field names and content keys.
 * Must stay in sync with SECTION_FIELD_MAP in useExperienceAutoSave.js.
 */
const SECTION_TYPE_TO_FIELD = {
  whats_included: { fieldName: 'whatsIncluded', wrapKey: 'items' },
  highlights: { fieldName: 'highlights', wrapKey: 'highlights' },
  what_to_bring: { fieldName: 'whatToBring', wrapKey: 'items' },
};

/**
 * Transform route API response data into wizard keyLocations format.
 * Converts server units (meters) to display units (miles/feet).
 *
 * @param {Array|null} routeData - GET /api/v1/experiences/:id/routes response data array
 * @returns {Object|null} keyLocations object compatible with wizard formData, or null
 */
export function transformRouteToKeyLocations(routeData) {
  if (!routeData || !Array.isArray(routeData) || routeData.length === 0) {
    return null;
  }

  const route = routeData[0]; // Primary route

  // Parse waypoints (handle both array and string)
  const waypoints = Array.isArray(route.waypoints)
    ? route.waypoints
    : (typeof route.waypoints === 'string' ? JSON.parse(route.waypoints) : []);

  // Extract meeting point: prefer 'start' or 'meeting_point' type, else first waypoint
  const startWaypoint = waypoints.find(
    (wp) => wp.type === 'start' || wp.type === 'meeting_point'
  ) || waypoints[0];

  const meeting_point = startWaypoint
    ? {
      name: startWaypoint.name || 'Meeting Point',
      latitude: startWaypoint.latitude,
      longitude: startWaypoint.longitude,
    }
    : {};

  // Route stats (meters → display units)
  const route_stats = {
    distance_miles: route.distanceMeters ? metersToMiles(route.distanceMeters) : null,
    elevation_gain_ft: route.totalElevationGainMeters
      ? metersToFeet(route.totalElevationGainMeters)
      : null,
    route_type: route.routeType || null,
  };

  return {
    meeting_point,
    waypoints: waypoints.map((wp) => ({
      name: wp.name || '',
      type: wp.type || 'waypoint',
      latitude: wp.latitude,
      longitude: wp.longitude,
      mile_marker: wp.mile_marker ?? null,
    })),
    route_stats,
    elevation_data: null,
    gpx_source: route.gpxFilename ? { filename: route.gpxFilename } : null,
    gpx_raw_xml: null, // Not loaded — prevents unnecessary re-persist (AC-06)
    route_overview: route.description || '',
    _originalRouteId: route.id,
  };
}

/**
 * Transform API response data into wizard formData format.
 *
 * @param {Object} experience - GET /api/v1/experiences/:id response data
 * @param {Object|null} taxonomy - GET /api/v1/taxonomy/experiences/:id response data
 * @param {Object|null} pricingConfig - GET /api/v1/guides/experiences/:id/pricing/config response data
 * @param {Object|null} sections - GET /api/v1/experiences/:id/sections response
 * @param {Array|null} routeData - GET /api/v1/experiences/:id/routes response data array
 * @returns {Object} formData compatible with ExperienceCreationWizard
 */
export function transformToWizardFormat(experience, taxonomy, pricingConfig, sections, routeData) {
  if (!experience) {
    return {};
  }

  const formData = {};

  // --- Stage 1: Basics ---
  formData.experienceName = experience.title || '';
  formData.description = experience.description || '';
  formData.difficultyLevel = experience.difficultyLevel || '';

  // Category: prefer taxonomy data (has L2/L3), fall back to experience detail (L1 only)
  const primaryCategory = extractPrimaryCategory(taxonomy);
  formData.categoryL1Id = primaryCategory.l1 || experience.category_l1_id || experience.categoryId || null;
  formData.categoryL2Id = primaryCategory.l2 || null;
  formData.categoryL3Id = primaryCategory.l3 || null;

  // --- Stage 2: Logistics ---
  formData.meetingPointAddress = experience.locationDetails?.name || '';
  formData.meetingLat = experience.locationDetails?.lat || null;
  formData.meetingLng = experience.locationDetails?.lng || null;
  formData.meetingState = experience.locationDetails?.state || '';
  formData.durationType = experience.durationType || 'hours';
  formData.durationHours = experience.durationHours || 4;
  formData.durationDays = experience.durationDays || null;
  formData.prepDays = experience.prepDays ?? 0;
  formData.cleanupDays = experience.cleanupDays ?? 0;
  formData.startDayRestrictions = experience.startDayRestrictions || {};
  formData.minGroupSize = experience.minParticipants ?? 1;
  formData.maxGroupSize = experience.maxParticipants ?? 4;

  // --- Stage 3: Pricing ---
  // Pricing comes from the pricing config endpoint (cents, not dollars)
  if (pricingConfig) {
    const formulaType = pricingConfig.pricing_model_type;
    formData.pricingModel = FORMULA_TYPE_TO_WIZARD[formulaType] || 'per_person';
    formData.basePriceCents = pricingConfig.base_rate_cents || 0;
    formData.perPersonPriceCents = pricingConfig.additional_rate_cents || 0;
    formData.includedCount = pricingConfig.included_count || 2;
  } else {
    // Fallback: use experience detail pricePerPerson (already in cents via ExperienceListService)
    formData.pricingModel = 'per_person';
    formData.basePriceCents = experience.pricePerPerson || 0;
    formData.perPersonPriceCents = 0;
  }
  formData.bookingLeadTime = experience.bookingLeadTimeHours ?? 24;

  // --- Stage 4: Media ---
  // Images from the experience detail response
  const { coverPhoto, galleryPhotos } = extractPhotos(experience);
  formData.coverPhoto = coverPhoto;
  formData.galleryPhotos = galleryPhotos;

  // --- Section data (highlights, whatsIncluded, whatToBring) ---
  const sectionData = extractSectionData(sections);
  formData.highlights = sectionData.highlights;
  formData.whatsIncluded = sectionData.whatsIncluded;
  formData.whatToBring = sectionData.whatToBring;

  // --- Key Locations (SCRUM-832: route data → wizard keyLocations) ---
  const keyLocations = transformRouteToKeyLocations(routeData);
  if (keyLocations) {
    formData.keyLocations = keyLocations;
  }

  // --- Metadata ---
  formData.isDraft = false;
  formData.lastSavedAt = null;

  return formData;
}

/**
 * Extract primary category L1/L2/L3 IDs from taxonomy response.
 */
function extractPrimaryCategory(taxonomy) {
  if (!taxonomy || !taxonomy.categories || !Array.isArray(taxonomy.categories)) {
    return { l1: null, l2: null, l3: null };
  }

  const primary = taxonomy.categories.find((c) => c.is_primary);
  const cat = primary || taxonomy.categories[0];

  if (!cat) {
    return { l1: null, l2: null, l3: null };
  }

  return {
    l1: cat.category_l1_id || null,
    l2: cat.category_l2_id || null,
    l3: cat.category_l3_id || null,
  };
}

/**
 * Extract cover photo and gallery photos from experience detail response.
 * The experience detail includes images[] (flat URLs, cover first) and
 * assets[] (full StoredAsset objects with asset_purpose).
 */
function extractPhotos(experience) {
  // Prefer assets[] with purpose field for accurate separation
  if (experience.assets && Array.isArray(experience.assets) && experience.assets.length > 0) {
    const coverAsset = experience.assets.find((a) => a.asset_purpose === 'cover');
    const galleryAssets = experience.assets.filter((a) => a.asset_purpose === 'gallery');

    return {
      coverPhoto: coverAsset?.cdn_url || coverAsset?.url || null,
      galleryPhotos: galleryAssets.map((a) => a.cdn_url || a.url).filter(Boolean),
    };
  }

  // Fallback: images[] array (cover is first element)
  if (experience.images && Array.isArray(experience.images) && experience.images.length > 0) {
    return {
      coverPhoto: experience.images[0],
      galleryPhotos: experience.images.slice(1),
    };
  }

  return { coverPhoto: null, galleryPhotos: [] };
}

/**
 * Extract section data (highlights, whatsIncluded, whatToBring) from sections response.
 */
function extractSectionData(sections) {
  const result = {
    highlights: [],
    whatsIncluded: [],
    whatToBring: [],
  };

  if (!sections || !sections.sections || !Array.isArray(sections.sections)) {
    return result;
  }

  for (const section of sections.sections) {
    const mapping = SECTION_TYPE_TO_FIELD[section.section_type_name];
    if (mapping && section.content_data) {
      const items = section.content_data[mapping.wrapKey];
      if (items && Array.isArray(items)) {
        result[mapping.fieldName] = items;
      }
    }
  }

  return result;
}
